/**
 * React Query hooks for product catalog.
 */
import { useQuery } from '@tanstack/react-query'
import { productsApi } from '@/services/products'

export const productKeys = {
  all: ['products'] as const,
  list: () => [...productKeys.all, 'list'] as const,
  sizes: (productId?: number) =>
    [...productKeys.all, 'sizes', productId ?? 'all'] as const,
  bundles: () => [...productKeys.all, 'bundles'] as const,
  appriseVmOptions: () => [...productKeys.all, 'apprise-vm-options'] as const,
}

export function useProducts() {
  return useQuery({
    queryKey: productKeys.list(),
    // the catalog is edited in Admin: never show a copy that is up to 5 minutes old when a wizard opens
    refetchOnMount: 'always',
    queryFn: async () => {
      const res = await productsApi.list()
      // Products API has no pagination (full catalog returned as array)
      return Array.isArray(res.data) ? res.data : res.data.results
    },
  })
}

export function useSizeConfigs(productId?: number) {
  return useQuery({
    queryKey: productKeys.sizes(productId),
    // sizes (VM profile, user count, ...) are edited in Admin: re-read them every time the wizard opens
    refetchOnMount: 'always',
    queryFn: async () => {
      const res = await productsApi.getSizeConfigs(productId)
      return Array.isArray(res.data) ? res.data : (res.data.results ?? [])
    },
  })
}

export function useBundles() {
  return useQuery({
    queryKey: productKeys.bundles(),
    queryFn: async () => {
      const res = await productsApi.getBundles()
      return res.data.results
    },
  })
}

/** Apprise-on-VM wizard options (image catalog, disk types, access methods, zonal regions). */
export function useAppriseVmOptions(enabled: boolean) {
  return useQuery({
    queryKey: productKeys.appriseVmOptions(),
    queryFn: async () => {
      const res = await productsApi.getAppriseVmOptions()
      return res.data
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  })
}
